export type UserRole = 'student' | 'teacher' | 'admin' | 'dev';
export type SchoolCode = 'KBA' | 'TLC';

export interface AuthUser {
  id: number;
  name: string;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
  announcement_email?: string | null;
  recovery_email?: string | null;
  role: UserRole;
  school?: SchoolCode;
  form?: number | null;
  login_id?: string;
  avatar_url?: string | null;
  phone_number?: string | null;
  demo_mode?: boolean;
  demo_branding?: Array<{
    school: SchoolCode;
    display_name: string | null;
    logo_url: string | null;
  }>;
}

// School theme colors
export interface SchoolTheme {
  primaryColor: string;
  primaryLight: string;
  primaryDark: string;
  secondaryColor: string;
  accentColor: string;
  logoIcon: string;
}

// School information for theming and display
export interface SchoolInfo {
  code: SchoolCode;
  name: string;
  shortName: string;
  type: 'secondary' | 'primary';
  description: string;
  theme: SchoolTheme;
}

export const SCHOOLS: Record<SchoolCode, SchoolInfo> = {
  KBA: {
    code: 'KBA',
    name: 'Kingdom Builder Academy',
    shortName: 'KBA',
    type: 'secondary',
    description: 'Secondary School',
    theme: {
      primaryColor: '#1e3a5f',
      primaryLight: '#2d5a8a',
      primaryDark: '#0f1d30',
      secondaryColor: '#2d5a8a',
      accentColor: '#d4af37',
      logoIcon: '/kba logo.PNG',
    },
  },
  TLC: {
    code: 'TLC',
    name: 'The Learning Center',
    shortName: 'TLC',
    type: 'primary',
    description: 'Primary & Nursery School',
    theme: {
      primaryColor: '#2d7d46',
      primaryLight: '#3da65c',
      primaryDark: '#1a4d2b',
      secondaryColor: '#3da65c',
      accentColor: '#f59e0b',
      logoIcon: '/tlc logo.jpg',
    },
  },
};

export type TargetGroup = 'student' | 'teacher' | 'admin' | 'all' | 'clubs';

export interface Club {
  id: number;
  name: string;
  description?: string | null;
  status: 'active' | 'inactive';
  created_by: number;
  created_at: string;
  updated_at: string;
  members_count?: number;
  creator?: {
    id: number;
    name: string;
    email: string;
  };
}

export interface ClubMember {
  id: number;
  name: string;
  email: string;
  student_id?: string | null;
  form?: number | null;
  pivot?: {
    role: string;
    joined_at: string;
  };
}

export interface Announcement {
  id: number;
  title: string;
  message: string;
  target_group: TargetGroup;
  club_id?: number | null;
  club?: Club | null;
  created_by: number;
  created_at: string;
  updated_at: string;
  creator?: {
    id: number;
    name: string;
    email: string;
  };
}

export interface ClassAnnouncement {
  id: number;
  school: SchoolCode;
  title: string;
  message: string;
  form: number;
  email_sent: boolean;
  created_by: number;
  created_at: string;
  updated_at: string;
  creator?: {
    id: number;
    name: string;
    email: string;
  };
}

export interface AssignedClass {
  form: number;
  subjects: string[];
}

export interface SchoolSettings {
  id: number;
  school: SchoolCode;
  current_academic_year: string;
  current_term: number;
  term_start_date: string | null;
  term_end_date: string | null;
  next_term_opening: string | null;
  boarding_locked: boolean;
  created_at: string;
  updated_at: string;
}

